import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

// Severity dot colors
export const SEV_COLOR = {
  CRITICAL: 'var(--negative)',
  HIGH:     'var(--warning)',
  MEDIUM:   'var(--accent)',
  LOW:      'var(--text-4)',
};

// CSS chevron helper (border-right + border-bottom; rotate for direction)
function Chevron({ open, color = 'var(--text-4)' }) {
  return (
    <span style={{
      display: 'inline-block', width: 6, height: 6,
      borderRight: `1.5px solid ${color}`, borderBottom: `1.5px solid ${color}`,
      transform: open ? 'rotate(45deg)' : 'rotate(-45deg)',
      transition: 'transform .2s',
      flexShrink: 0,
    }} />
  );
}

export default function Dashboard({ onSelectProject, onNavigate, API }) {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "", client: "", description: "", priority: "medium", pitch_template: "",
    subject_line: "", follow_up_message: "", industry: "", topic: "", campaign_id: "", ticker: ""
  });
  const [autoFollowups, setAutoFollowups] = useState(false);
  const [toast, setToast] = useState(null);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showArchives, setShowArchives] = useState(false);
  const [selectedArchived, setSelectedArchived] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProjectsCache, setAllProjectsCache] = useState(null);
  const [showSearchDrop, setShowSearchDrop] = useState(false);
  const [allExpertsCache, setAllExpertsCache] = useState(null);
  const [expertResults, setExpertResults] = useState([]);
  const [raspAI, setRaspAI] = useState(null);
  const [raspAILoading, setRaspAILoading] = useState(true);
  const [raspAIExpanded, setRaspAIExpanded] = useState(true);
  const [emailExecuteInFlight, setEmailExecuteInFlight] = useState(new Set());
  const [topbarSlot, setTopbarSlot] = useState(null);
  const searchContainerRef = useRef(null);

  useEffect(() => { fetchProjects(); fetchStats(); fetchArchivedProjects(); }, []);

  useLayoutEffect(() => {
    const slot = document.getElementById('topbar-actions');
    if (slot) setTopbarSlot(slot);
  }, []);

  const fetchRaspAI = async () => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const _API = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      const res = await fetch(`${_API}/api/dashboard/rasp-ai`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRaspAI(data);
      }
    } catch (e) {
      console.error('Rasp AI fetch error:', e);
    } finally {
      setRaspAILoading(false);
    }
  };

  useEffect(() => { fetchRaspAI(); }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSearchDrop(false);
      }
    };
    const handleEscape = (e) => { if (e.key === 'Escape') setShowSearchDrop(false); };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setProjects(await res.json());
    } catch (e) { console.error(e); }
  };

  const unarchiveProject = async (projectId) => {
    await fetch(`${API}/projects/${projectId}/unarchive`, { method: 'PATCH', headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
    fetchProjects();
    fetchArchivedProjects();
  };

  const fetchArchivedProjects = async () => {
    const token = localStorage.getItem('expertnet_token');
    const res = await fetch(`${API}/projects/archived`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setArchivedProjects(await res.json());
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const createProject = async () => {
    if (!newProject.name.trim()) { showToast("Project name required", "error"); return; }
    try {
      const res = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ ...newProject, auto_followups: autoFollowups ? 1 : 0 }),
      });
      const data = await res.json();
      if (data.id) {
        showToast("Project created!");
        setShowNewProject(false);
        setNewProject({ name: "", client: "", description: "", priority: "medium", pitch_template: "",
          subject_line: "", follow_up_message: "", industry: "", topic: "", campaign_id: "", ticker: "" });
        setAutoFollowups(false);
        fetchProjects();
        fetchStats();
      }
    } catch (e) { showToast("Failed to create project", "error"); }
  };

  const archiveProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to archive this project?')) return;
    const token = localStorage.getItem('expertnet_token');
    try {
      const res = await fetch(`${API}/projects/${projectId}/archive`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { showToast('Failed to archive project. Please try again.', 'error'); return; }
      fetchProjects();
      fetchArchivedProjects();
    } catch (e) { showToast('Failed to archive project. Please try again.', 'error'); }
  };

  const handleSearchChange = async (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (!q.trim()) { setShowSearchDrop(false); setExpertResults([]); return; }
    const token = localStorage.getItem('expertnet_token');
    const headers = { Authorization: `Bearer ${token}` };
    let pool = allProjectsCache;
    if (!pool) {
      const [activeRes, archivedRes] = await Promise.all([
        fetch(`${API}/projects`, { headers }),
        fetch(`${API}/projects/archived`, { headers }),
      ]);
      const active = activeRes.ok ? await activeRes.json() : [];
      const archived = archivedRes.ok ? await archivedRes.json() : [];
      pool = [...active, ...archived];
      setAllProjectsCache(pool);
    }
    const lower = q.toLowerCase();
    const filtered = pool
      .filter(p => p.name?.toLowerCase().includes(lower) || p.ticker?.toLowerCase().includes(lower))
      .slice(0, 8);
    setSearchResults(filtered);
    let expertPool = allExpertsCache;
    if (!expertPool) {
      try {
        const res = await fetch(`${API}/experts/all`, { headers });
        expertPool = res.ok ? await res.json() : [];
      } catch (_) { expertPool = []; }
      setAllExpertsCache(expertPool);
    }
    setExpertResults(
      expertPool
        .filter(ex => ex.name?.toLowerCase().includes(lower) || ex.company?.toLowerCase().includes(lower))
        .slice(0, 6)
    );
    setShowSearchDrop(true);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      onSelectProject(searchResults[0]);
      setShowSearchDrop(false);
      setSearchQuery('');
    }
  };

  const [expandedGroups, setExpandedGroups] = useState({ 'High Priority': false, 'Active': false, 'Long Term': false, 'On Hold': false, 'Other (unknown priority)': false });
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const handleSort = (key) => setSortConfig(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const PRIORITY_KEY_MAP = {
    'High Priority': ['High Priority', 'high'],
    'Active': ['Medium', 'medium'],
    'Long Term': ['Long-Term', 'low'],
    'On Hold': ['No Priority', '', null, undefined],
  };

  const PRIORITY_RECOGNIZED = new Set(
    Object.values(PRIORITY_KEY_MAP).flat().filter(v => v != null)
  );

  const PRIORITY_GROUPS = [
    { key: 'High Priority', color: '#e06a5a' },
    { key: 'Active',        color: '#2dd4bf' },
    { key: 'Long Term',     color: '#0e7490' },
    { key: 'On Hold',       color: '#4a6878' },
    { key: 'Other (unknown priority)', color: '#6b7280' },
  ];

  const handleRaspAISnooze = async (projectId, action) => {
    try {
      const _API = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      await fetch(`${_API}/api/projects/${projectId}/rasp-ai-snooze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ action }),
      });
      setRaspAI(prev => prev ? { ...prev, items: prev.items.filter(i => !((i.trigger === 5 || i.trigger === 6) && i.project_id === projectId)) } : prev);
    } catch (e) { console.error('Snooze error:', e); }
  };

  const handleProjectEmailExecute = async (projectId, projectName, expertCount) => {
    if (!window.confirm(`Send email follow-ups to ${expertCount} pitched expert${expertCount !== 1 ? 's' : ''} in "${projectName}"?`)) return;
    setEmailExecuteInFlight(prev => new Set(prev).add(projectId));
    try {
      const token = localStorage.getItem('expertnet_token');
      const _API = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      const projRes = await fetch(`${_API}/api/projects/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!projRes.ok) { alert('Failed to load project experts'); return; }
      const projData = await projRes.json();
      const pitchedIds = (projData.experts || []).filter(e => e.status === 'pitched').map(e => e.id);
      if (!pitchedIds.length) { alert('No pitched experts found'); return; }
      const res = await fetch(`${_API}/api/projects/${projectId}/send-email-followups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ expert_ids: pitchedIds }),
      });
      const data = await res.json();
      if (res.ok) {
        const parts = [`${data.sent} sent`];
        if (data.skipped_count) parts.push(`${data.skipped_count} skipped`);
        if (data.pitched_no_email_count) parts.push(`${data.pitched_no_email_count} no email`);
        setToast({ message: parts.join(' · '), type: 'success' });
        setTimeout(() => setToast(null), 4000);
        await fetchRaspAI();
      } else {
        alert(data.error || 'Failed to send follow-ups');
      }
    } catch (e) {
      console.error('Email execute error:', e);
      alert('Network error — follow-ups not sent');
    } finally {
      setEmailExecuteInFlight(prev => { const s = new Set(prev); s.delete(projectId); return s; });
    }
  };

  const handleLinkedinTouch = async (expertId, projectId, trigger) => {
    try {
      const _API = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${_API}/api/projects/${projectId}/experts/${expertId}/linkedin-touch`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setRaspAI(prev => prev ? { ...prev, items: prev.items.filter(i => !(i.expert_id === expertId && i.trigger === trigger)) } : prev);
      } else {
        alert(data.error || 'Failed to log LinkedIn touch');
      }
    } catch (e) { console.error('LinkedIn touch error:', e); }
  };

  const handleEngagedSnooze = async (expertId, projectId) => {
    try {
      const _API = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${_API}/api/projects/${projectId}/experts/${expertId}/engaged-snooze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setRaspAI(prev => prev ? { ...prev, items: prev.items.filter(i => !(i.expert_id === expertId && i.trigger === 3)) } : prev);
      } else {
        alert(data.error || 'Failed to snooze expert');
      }
    } catch (e) { console.error('Engaged snooze error:', e); }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  // ── Controls (search + Archives + New Project) ────────────────────────────────
  const controls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {/* Search field */}
      <div ref={searchContainerRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--input-bg)', border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px', width: 240, height: 32 }}>
        {/* CSS search glyph: 11px circle + handle */}
        <span style={{ position: 'relative', display: 'inline-block', width: 13, height: 13, flexShrink: 0 }}>
          <span style={{ display: 'block', position: 'absolute', width: 9, height: 9, border: '1.5px solid var(--text-3)', borderRadius: '50%', top: 0, left: 0 }} />
          <span style={{ display: 'block', position: 'absolute', width: 4, height: 1.5, background: 'var(--text-3)', bottom: 1, right: 0, transform: 'rotate(45deg)', transformOrigin: 'bottom right', borderRadius: 1 }} />
        </span>
        <input
          className="dash-search"
          type="text"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-1)', fontSize: 13, outline: 'none', padding: 0 }}
        />
        {showSearchDrop && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--glass-sidebar)', border: '1px solid var(--line)', borderRadius: 8, boxShadow: 'var(--card-shadow)', zIndex: 1000, overflow: 'hidden', backdropFilter: 'var(--glass-blur)', WebkitBackdropFilter: 'var(--glass-blur)' }}>
            {searchResults.length === 0 && expertResults.length === 0 ? (
              <div style={{ padding: '10px 14px', color: 'var(--text-3)', fontSize: 13 }}>No matches found</div>
            ) : (
              <>
                {searchResults.length > 0 && (
                  <>
                    {expertResults.length > 0 && (
                      <div className="r-eyebrow" style={{ padding: '6px 14px 2px' }}>Projects</div>
                    )}
                    {searchResults.map(p => (
                      <div
                        key={p.id}
                        onClick={() => { onSelectProject(p); setShowSearchDrop(false); setSearchQuery(''); }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: 'transparent' }}
                      >
                        <span style={{ color: 'var(--text-1)', fontSize: 13, flex: 1 }}>{p.name}</span>
                        {p.ticker && <span style={{ fontFamily: 'var(--font-data)', color: 'var(--accent)', fontSize: 11 }}>{p.ticker}</span>}
                      </div>
                    ))}
                  </>
                )}
                {expertResults.length > 0 && (
                  <>
                    {searchResults.length > 0 && <div style={{ height: 1, background: 'var(--line-soft)', margin: '2px 0' }} />}
                    <div className="r-eyebrow" style={{ padding: '6px 14px 2px' }}>Experts</div>
                    {expertResults.map(ex => (
                      <div
                        key={ex.pe_id || ex.id}
                        onClick={() => { onSelectProject({ id: ex.project_id }); setShowSearchDrop(false); setSearchQuery(''); setExpertResults([]); }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, background: 'transparent' }}
                      >
                        <span style={{ color: 'var(--text-1)', fontSize: 13 }}>{ex.name}</span>
                        <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{[ex.company, ex.project_name].filter(Boolean).join(' · ')}</span>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <button className="r-btn r-btn-ghost" onClick={() => setShowArchives(v => !v)}>
        {showArchives ? '← Back' : 'Archives'}
      </button>
      <button className="r-btn r-btn-primary" onClick={() => setShowNewProject(true)}>
        + New project
      </button>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`.dash-search::placeholder { color: var(--text-3); }`}</style>

      {topbarSlot ? createPortal(controls, topbarSlot) : null}

      <div style={{ padding: '24px', maxWidth: 1440, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Inline controls fallback (no topbar slot) */}
        {!topbarSlot && <div>{controls}</div>}

        {/* ── Rasp AI Banner ─────────────────────────────────────────────── */}
        <div style={{ borderRadius: 12, border: '1px solid rgba(45,212,191,.35)', background: 'linear-gradient(90deg, rgba(14,116,144,.38), rgba(45,212,191,.10) 45%, rgba(10,22,40,.6))', backdropFilter: 'var(--glass-blur)', WebkitBackdropFilter: 'var(--glass-blur)', padding: '14px 18px' }}>

          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Icon square with pulsing dot */}
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#5eead4,#14b8a6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'rasp-pulse 2.4s ease-in-out infinite' }} />
            </div>

            {/* Title + eyebrow + LIVE */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>Rasp AI</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--accent)' }}>Intelligence</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--positive)', display: 'inline-block' }} />
                  <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '.08em' }}>LIVE</span>
                </span>
              </div>
              {/* Summary sentence */}
              {!raspAILoading && raspAI?.items?.length > 0 && (() => {
                const criticals = raspAI.items.filter(i => i.severity === 'CRITICAL');
                const topItem = raspAI.items[0];
                return (
                  <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.45 }}>
                    {criticals.length > 0
                      ? <span>You have <span style={{ fontFamily: 'var(--font-data)', color: 'var(--negative)', fontWeight: 600 }}>{criticals.length}</span> engaged {criticals.length === 1 ? 'expert' : 'experts'} going cold.</span>
                      : <span style={{ color: 'var(--text-3)' }}><span style={{ fontFamily: 'var(--font-data)' }}>{raspAI.items.length}</span> action{raspAI.items.length === 1 ? '' : 's'} in your queue.</span>
                    }
                    {topItem?.expert_name
                      ? <> Highest priority: <span style={{ fontFamily: 'var(--font-data)', color: 'var(--text-1)', fontWeight: 500 }}>{topItem.expert_name}</span> at <span style={{ fontFamily: 'var(--font-data)', color: 'var(--text-1)', fontWeight: 500 }}>{topItem.project_name}</span> — <span style={{ fontFamily: 'var(--font-data)', color: 'var(--negative)' }}>{Math.floor(topItem.days_stale)}</span> days silent.</>
                      : topItem?.project_name
                        ? <> Top action: <span style={{ fontFamily: 'var(--font-data)', color: 'var(--text-1)', fontWeight: 500 }}>{topItem.project_name}</span>.</>
                        : null
                    }
                  </div>
                );
              })()}
              {!raspAILoading && !raspAI?.items?.length && (
                <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 3 }}>Pipeline looks clear. Check back later.</div>
              )}
              {raspAILoading && (
                <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 3 }}>Analyzing your pipeline...</div>
              )}
            </div>

            {/* Right: collapse */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <button
                onClick={() => setRaspAIExpanded(v => !v)}
                style={{ width: 28, height: 28, background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, flexShrink: 0 }}
              >
                <Chevron open={raspAIExpanded} />
              </button>
            </div>
          </div>

          {/* Expanded item list (capped at 8) */}
          {raspAIExpanded && !raspAILoading && raspAI?.items?.length > 0 && (
            <div style={{ maxHeight: 340, overflowY: 'auto', overflowX: 'hidden', marginTop: 10 }}>
              {raspAI.items.slice(0, 8).map((item, idx) => {
                const canNavigate = item.project_id != null;
                const sevColor = SEV_COLOR[item.severity] || 'var(--text-4)';
                return (
                  <div
                    key={idx}
                    onClick={() => { if (canNavigate) { const proj = projects.find(p => p.id === item.project_id); if (proj) onSelectProject(proj); } }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40, borderTop: '1px solid var(--line-soft)', padding: '4px 0', cursor: canNavigate ? 'pointer' : 'default', background: 'transparent', transition: 'background .1s' }}
                    onMouseEnter={e => { if (canNavigate) e.currentTarget.style.background = 'var(--row-hover)'; }}
                    onMouseLeave={e => { if (canNavigate) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Severity dot + label */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, width: 80 }}>
                      <span className="r-dot" style={{ color: sevColor }} />
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.08em', color: sevColor }}>{item.severity}</span>
                    </div>
                    {/* Name · project, action label */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.expert_name
                          ? <>{item.expert_name}<span style={{ color: 'var(--text-5)', margin: '0 4px' }}>·</span><span style={{ color: 'var(--text-3)' }}>{item.project_name}</span></>
                          : item.project_name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.action_label}</div>
                    </div>
                    {/* Days stale */}
                    {typeof item.days_stale === 'number' && (
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>{Math.floor(item.days_stale)}d silent</span>
                    )}
                    {/* Action buttons */}
                    {item.trigger === 3 && (
                      <button className="r-btn r-btn-ghost" style={{ height: 24, padding: '0 10px', fontSize: 11 }}
                        onClick={e => { e.stopPropagation(); handleEngagedSnooze(item.expert_id, item.project_id); }}>Snooze</button>
                    )}
                    {(item.trigger === 61 || item.trigger === 62 || item.trigger === 63) && (
                      <button className="r-btn r-btn-primary" style={{ height: 24, padding: '0 10px', fontSize: 11 }}
                        onClick={e => { e.stopPropagation(); handleLinkedinTouch(item.expert_id, item.project_id, item.trigger); }}>Mark Sent</button>
                    )}
                    {item.trigger === 2 && (
                      <button className="r-btn r-btn-primary" style={{ height: 24, padding: '0 10px', fontSize: 11, opacity: emailExecuteInFlight.has(item.project_id) ? 0.6 : 1 }}
                        disabled={emailExecuteInFlight.has(item.project_id)}
                        onClick={e => { e.stopPropagation(); handleProjectEmailExecute(item.project_id, item.project_name, item.expert_count); }}>
                        {emailExecuteInFlight.has(item.project_id) ? '…' : 'Execute'}
                      </button>
                    )}
                    {(item.trigger === 5 || item.trigger === 6) && (
                      <button className="r-btn r-btn-ghost" style={{ height: 24, padding: '0 10px', fontSize: 11 }}
                        onClick={e => { e.stopPropagation(); handleRaspAISnooze(item.project_id, '2days'); }}>Snooze</button>
                    )}
                    {/* Navigable chevron */}
                    {canNavigate && <Chevron open={false} color="var(--text-5)" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* View All button — visible when expanded and not loading */}
          {raspAIExpanded && !raspAILoading && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                className="r-btn r-btn-ghost"
                style={{ height: 28 }}
                onClick={() => onNavigate('rasp-ai-all')}
              >View All</button>
            </div>
          )}
        </div>

        {/* ── Stats strip ────────────────────────────────────────────────── */}
        <div className="r-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--line)' }}>
          {[
            { label: 'Projects',  value: stats.projects  || 0, accent: false },
            { label: 'Experts',   value: stats.experts   || 0, accent: false },
            { label: 'Engaged',   value: stats.engaged   || 0, accent: true  },
            { label: 'Confirmed', value: stats.confirmed || 0, accent: false },
          ].map(s => (
            <div key={s.label} style={{ padding: '16px 20px', background: 'linear-gradient(180deg, rgba(22,42,66,.66), rgba(10,22,40,.60))' }}>
              <div className="r-eyebrow" style={{ marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--font-data)', fontSize: 30, fontWeight: 500, letterSpacing: '-0.02em', color: s.accent ? 'var(--accent)' : 'var(--text-1)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {s.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        {/* ── Projects section ───────────────────────────────────────────── */}
        {showArchives ? (
          /* Archives view */
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>Archived projects</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', border: '1px solid var(--line)', borderRadius: 999, padding: '1px 8px' }}>{archivedProjects.length}</span>
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ accentColor: 'var(--accent)' }}
                  checked={archivedProjects.length > 0 && selectedArchived.size === archivedProjects.length}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedArchived(new Set(archivedProjects.map(p => p.id)));
                    else setSelectedArchived(new Set());
                  }}
                />
                Select All
              </label>
            </div>
            {archivedProjects.length > 0 ? (
              <div className="r-card">
                {archivedProjects.map((p, i) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, height: 44, padding: '0 18px', borderBottom: i < archivedProjects.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                    <input
                      type="checkbox"
                      style={{ accentColor: 'var(--accent)' }}
                      checked={selectedArchived.has(p.id)}
                      onChange={(e) => {
                        const next = new Set(selectedArchived);
                        if (e.target.checked) next.add(p.id); else next.delete(p.id);
                        setSelectedArchived(next);
                      }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', flex: 1 }}>{p.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{p.client}</span>
                    <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</span>
                    <button
                      className="r-btn r-btn-ghost"
                      style={{ height: 24, padding: '0 10px', fontSize: 11 }}
                      onClick={() => unarchiveProject(p.id)}
                    >Unarchive</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '24px 0' }}>No archived projects.</div>
            )}
          </div>
        ) : (
          /* Active projects view */
          <div>
            {/* Header row with column captions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>Active projects</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', border: '1px solid var(--line)', borderRadius: 999, padding: '1px 8px' }}>{projects.length}</span>
              </div>
              {/* Column captions aligned over group totals */}
              <div style={{ display: 'flex', alignItems: 'center', paddingRight: 60 }}>
                {['PROJ', 'EXP', 'PITCH', 'ENG', 'CON'].map(cap => (
                  <span key={cap} style={{ fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '.08em', width: 64, textAlign: 'right' }}>
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            {/* Priority groups */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {PRIORITY_GROUPS.map((group) => {
                const groupProjects = projects
                  .filter(p => group.key === 'Other (unknown priority)'
                    ? (p.priority != null && !PRIORITY_RECOGNIZED.has(p.priority))
                    : PRIORITY_KEY_MAP[group.key].includes(p.priority || null))
                  .sort((a, b) => {
                    const ta = new Date(a.last_activity || a.updated_at || 0).getTime();
                    const tb = new Date(b.last_activity || b.updated_at || 0).getTime();
                    return tb - ta;
                  });

                if (group.key === 'Other (unknown priority)' && groupProjects.length === 0) return null;

                const totalExperts   = groupProjects.reduce((s, p) => s + (p.expert_count    || 0), 0);
                const totalPitched   = groupProjects.reduce((s, p) => s + (p.pitched_count   || 0), 0);
                const totalEngaged   = groupProjects.reduce((s, p) => s + (p.engaged_count   || 0), 0);
                const totalConfirmed = groupProjects.reduce((s, p) => s + (p.confirmed_count || 0), 0);

                const isExpanded = expandedGroups[group.key];
                const toggle = () => setExpandedGroups(prev => ({ ...prev, [group.key]: !prev[group.key] }));

                const sorted = sortConfig.key ? [...groupProjects].sort((a, b) => {
                  if (sortConfig.key === 'ticker')       { const av = (a.ticker||'').toLowerCase(), bv = (b.ticker||'').toLowerCase(); return sortConfig.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
                  if (sortConfig.key === 'name')         { const av = (a.name||'').toLowerCase(),   bv = (b.name||'').toLowerCase();   return sortConfig.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
                  if (sortConfig.key === 'client')       { const av = (a.client||'').toLowerCase(), bv = (b.client||'').toLowerCase(); return sortConfig.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); }
                  if (sortConfig.key === 'expert_count') { return sortConfig.dir === 'asc' ? (a.expert_count||0) - (b.expert_count||0) : (b.expert_count||0) - (a.expert_count||0); }
                  if (sortConfig.key === 'pitched_count'){ return sortConfig.dir === 'asc' ? (a.pitched_count||0) - (b.pitched_count||0) : (b.pitched_count||0) - (a.pitched_count||0); }
                  if (sortConfig.key === 'last_activity') {
                    const at = (a.last_activity || a.updated_at) ? new Date(a.last_activity || a.updated_at).getTime() : 0;
                    const bt = (b.last_activity || b.updated_at) ? new Date(b.last_activity || b.updated_at).getTime() : 0;
                    if (!at) return 1; if (!bt) return -1;
                    return sortConfig.dir === 'asc' ? at - bt : bt - at;
                  }
                  return 0;
                }) : groupProjects;

                return (
                  <div key={group.key} className="r-card" style={{ border: `1px solid ${isExpanded ? 'rgba(45,212,191,.35)' : 'var(--line)'}` }}>
                    {/* Group header */}
                    <div
                      onClick={toggle}
                      style={{ display: 'flex', alignItems: 'center', height: 44, padding: '0 18px', cursor: 'pointer', userSelect: 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.03)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {/* Status dot with glow */}
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: group.color, boxShadow: `0 0 8px ${group.color}`, marginRight: 10, flexShrink: 0 }} />
                      {/* Group name */}
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', flex: 1 }}>{group.key}</span>
                      {/* Five 64px mono totals */}
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, width: 64, textAlign: 'right', color: 'var(--text-3)' }}>{groupProjects.length > 0 ? groupProjects.length : <span style={{ color: 'var(--text-5)' }}>—</span>}</span>
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, width: 64, textAlign: 'right', color: 'var(--text-3)' }}>{totalExperts > 0 ? totalExperts : <span style={{ color: 'var(--text-5)' }}>—</span>}</span>
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, width: 64, textAlign: 'right', color: 'var(--text-3)' }}>{totalPitched > 0 ? totalPitched : <span style={{ color: 'var(--text-5)' }}>—</span>}</span>
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, width: 64, textAlign: 'right', color: totalEngaged > 0 ? 'var(--accent)' : 'var(--text-5)' }}>{totalEngaged > 0 ? totalEngaged : '—'}</span>
                      <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, width: 64, textAlign: 'right', color: totalConfirmed > 0 ? 'var(--positive)' : 'var(--text-5)', marginRight: 12 }}>{totalConfirmed > 0 ? totalConfirmed : '—'}</span>
                      {/* CSS chevron */}
                      <Chevron open={isExpanded} />
                    </div>

                    {/* Group body: project table */}
                    {isExpanded && groupProjects.length > 0 && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '64px' }} />
                          <col />
                          <col style={{ width: '110px' }} />
                          <col style={{ width: '64px' }} />
                          <col style={{ width: '64px' }} />
                          <col style={{ width: '64px' }} />
                          <col style={{ width: '64px' }} />
                          <col style={{ width: '110px' }} />
                          <col style={{ width: '84px' }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: 'var(--subbar-bg)', height: 32 }}>
                            {[
                              { key: 'ticker',        label: 'TICKER' },
                              { key: 'name',          label: 'PROJECT' },
                              { key: 'client',        label: 'CLIENT' },
                              { key: 'expert_count',  label: 'EXP' },
                              { key: 'pitched_count', label: 'PITCH' },
                              { key: null,            label: 'ENG' },
                              { key: null,            label: 'CON' },
                              { key: 'last_activity', label: 'UPDATED' },
                              { key: null,            label: '' },
                            ].map((col, ci) => (
                              <th
                                key={ci}
                                onClick={col.key ? () => handleSort(col.key) : undefined}
                                style={{
                                  padding: '0 12px',
                                  fontFamily: 'var(--font-data)',
                                  fontSize: 10,
                                  fontWeight: 500,
                                  color: 'var(--text-4)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '.08em',
                                  textAlign: ci >= 3 && ci <= 7 ? 'right' : 'left',
                                  cursor: col.key ? 'pointer' : 'default',
                                  userSelect: 'none',
                                  borderBottom: '1px solid var(--line)',
                                  whiteSpace: 'nowrap',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {col.label}{col.key && sortConfig.key === col.key ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((project, ri) => (
                            <tr
                              key={project.id}
                              onClick={() => onSelectProject(project)}
                              style={{ cursor: 'pointer', height: 38, borderBottom: '1px solid var(--line-soft)', background: ri % 2 === 1 ? 'var(--row-odd)' : 'transparent' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
                              onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 1 ? 'var(--row-odd)' : 'transparent'}
                            >
                              <td style={{ padding: '0 12px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                {project.ticker
                                  ? <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--accent)' }}>{project.ticker}</span>
                                  : <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-5)' }}>—</span>
                                }
                              </td>
                              <td style={{ padding: '0 12px', fontSize: 13, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {project.name}
                              </td>
                              <td style={{ padding: '0 12px', fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {project.client || '—'}
                              </td>
                              <td style={{ padding: '0 12px', fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-2)', textAlign: 'right' }}>
                                {project.expert_count || 0}
                              </td>
                              <td style={{ padding: '0 12px', fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-2)', textAlign: 'right' }}>
                                {project.pitched_count || 0}
                              </td>
                              <td style={{ padding: '0 12px', fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--accent)', textAlign: 'right' }}>
                                {project.engaged_count || 0}
                              </td>
                              <td style={{ padding: '0 12px', fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-2)', textAlign: 'right' }}>
                                {project.confirmed_count || 0}
                              </td>
                              <td style={{ padding: '0 12px', fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-3)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {(() => {
                                  const d = project.last_activity ? new Date(project.last_activity) : null;
                                  if (!d) return <span style={{ color: 'var(--text-5)' }}>No activity</span>;
                                  const diff = Date.now() - d.getTime();
                                  const mins = Math.floor(diff / 60000);
                                  const hrs  = Math.floor(diff / 3600000);
                                  const days = Math.floor(diff / 86400000);
                                  if (mins < 60) return `${mins}m ago`;
                                  if (hrs  < 24) return `${hrs}h ago`;
                                  return `${days}d ago`;
                                })()}
                              </td>
                              <td style={{ padding: '0 12px', textAlign: 'right' }}>
                                <button
                                  className="r-btn r-btn-ghost"
                                  style={{ height: 24, padding: '0 10px', fontSize: 11 }}
                                  onClick={(e) => { e.stopPropagation(); archiveProject(project.id); }}
                                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(224,106,90,.1)'; e.currentTarget.style.color = 'var(--negative)'; e.currentTarget.style.borderColor = 'rgba(224,106,90,.3)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#c8d6e0'; e.currentTarget.style.borderColor = 'var(--line)'; }}
                                >Archive</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Empty group state */}
                    {isExpanded && groupProjects.length === 0 && (
                      <div style={{ padding: '12px 18px', fontSize: 12, color: 'var(--text-4)' }}>No projects</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── New Project modal (unchanged) ──────────────────────────────── */}
      {showNewProject && (
        <div className="modal-overlay" onClick={() => setShowNewProject(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>New Project</h2>
            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input className="form-input" placeholder="e.g. Oncology Drug Launch Strategy"
                value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">TICKER</label>
              <input className="form-input" placeholder="e.g. AAPL"
                value={newProject.ticker} onChange={(e) => setNewProject({ ...newProject, ticker: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Client</label>
              <input className="form-input" placeholder="e.g. Blackstone"
                value={newProject.client} onChange={(e) => setNewProject({ ...newProject, client: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Brief / Description</label>
              <textarea className="form-input" rows={3} placeholder="Describe the expert profile..."
                value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[{ val: "high", label: "High Priority", color: "#e06a5a" }, { val: "medium", label: "Active", color: "#2dd4bf" }, { val: "low", label: "Long Term", color: "#0e7490" }].map((opt) => (
                  <button key={opt.val} onClick={() => setNewProject({ ...newProject, priority: opt.val })}
                    style={{ flex: 1, height: 32, borderRadius: 8, border: newProject.priority === opt.val ? `1px solid ${opt.color}` : "1px solid var(--line)", background: newProject.priority === opt.val ? `${opt.color}1f` : "transparent", color: newProject.priority === opt.val ? opt.color : "var(--text-3)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Pitch Template</label>
              <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 6 }}>Use <code style={{ background: 'var(--input-bg)', padding: "1px 4px", borderRadius: 3 }}>[Name]</code> to insert the recipient's first name (any capitalization works). Other placeholders are not supported yet — write the rest as plain text.</p>
              <textarea className="form-input" rows={6}
                placeholder={"Hi [Name],\n\nI hope this message finds you well. I'm reaching out because I'm supporting an investment client who is looking to better understand the space. Given your background in the industry, I thought you might be a great resource...\n\nBest,"}
                value={newProject.pitch_template} onChange={(e) => setNewProject({ ...newProject, pitch_template: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Subject Line</label>
              <input className="form-input" placeholder="e.g. Quick question for you"
                value={newProject.subject_line} onChange={(e) => setNewProject({ ...newProject, subject_line: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">FOLLOW-UP MESSAGE</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Hey [Name], just wanted to follow up on my previous message..."
                value={newProject.follow_up_message}
                onChange={e => setNewProject({ ...newProject, follow_up_message: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <label className="form-label" style={{ margin: 0 }}>Automatic Follow-ups</label>
              <div
                onClick={() => setAutoFollowups(v => !v)}
                style={{ width: 40, height: 22, borderRadius: 11, background: autoFollowups ? "rgba(45,212,191,.6)" : "rgba(255,255,255,.12)", position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}
              >
                <div style={{ position: "absolute", top: 3, left: autoFollowups ? 21 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowNewProject(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createProject}>Create Project</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          <span className="r-dot" style={{ color: toast.type === 'error' ? 'var(--negative)' : 'var(--positive)' }} />
          {toast.msg || toast.message}
        </div>
      )}
    </>
  );
}
